import React from 'react'
import {ErrorPage} from '@freckle/student/ts/common/components/common/error-page'
import {LoggedRoute} from '@freckle/student/ts/common/routers/logged-route'
import type {Chrome} from '@freckle/student/ts/pages/chromeless-launch/components/use-chrome'
import type {TimeSpanQueryParamsT} from '../../components/navigation/common/logic/time-span'
import type {TabT} from '../../components/navigation/start-page-assignments/types/tabs'
import {
  StartPageHomeHandler,
  SelectLoginPageHandler,
  StartPageAssignmentsHandler,
  StorePageHandler,
  StorePageDebugHandler,
  StoreClosedPageHandler,
  StoreOpenPageHandler,
  ChangeThemeHandler,
  DebugDepthOfKnowledgeHandler,
  AssignmentNotFoundHandler,
  AssignmentErrorHandler,
  DebuggersPageHandler,
  LoginHandler,
  VideoPlayerHandler,
  LoginQrHandler,
  DirectLoginHandler,
  SignoutHandler,
  ClassCodeHelpPage,
  MathViewerHandler,
  OnboardingPageHandler
} from './common-lazy'
import type {PathData} from './path-data'

export const BYPASS_CHECK = 'bypassCheck'

export type CommonRoutesWithQueryParams = {
  '/login': undefined
  '/login-qr': undefined
  '/direct-login': undefined
  '/signout': undefined
  '/error': undefined
  '/class-code-help': undefined
  '/start': {teacher?: boolean}
  '/units/:unit/videos/:videoUrl': undefined
  '/select-class': undefined | {fromQr: true}
  '/depth-of-knowledge/debug/:questionSetUuid': undefined
  '/start/assignments': {
    tab?: TabT
    timeSpan?: TimeSpanQueryParamsT | null
  }
  '/store': {redirect?: string}
  '/store-closed': {redirect?: string}
  '/store-open': undefined
  '/change-theme': undefined
  '/debugStore': undefined
  '/assignments/:subject/not-found': {chrome: Chrome} | undefined
  '/assignments/:subject/error': {chrome: Chrome; message: string}
  '/debuggers': undefined
  '/debuggers/math/question-skills-math-viewer': undefined
  '/onboarding': {[BYPASS_CHECK]?: boolean}
}

export const commonPathData: PathData<CommonRoutesWithQueryParams> = {
  '/login': {isBlockedByFocusMode: false, element: () => <LoginHandler />},
  '/login-qr': {isBlockedByFocusMode: false, element: () => <LoginQrHandler />},
  '/direct-login': {isBlockedByFocusMode: false, element: () => <DirectLoginHandler />},
  '/signout': {isBlockedByFocusMode: false, element: () => <SignoutHandler />},
  '/error': {isBlockedByFocusMode: false, element: () => <ErrorPage />},
  '/class-code-help': {isBlockedByFocusMode: false, element: () => <ClassCodeHelpPage />},
  '/start': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={StartPageHomeHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    )
  },
  '/units/:unit/videos/:videoUrl': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={VideoPlayerHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    )
  },
  '/select-class': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={SelectLoginPageHandler}
        wrapperParams={{isFullScreen: true, isResponsive: true}}
        path={path}
      />
    )
  },
  '/depth-of-knowledge/debug/:questionSetUuid': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={DebugDepthOfKnowledgeHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    )
  },
  '/start/assignments': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={StartPageAssignmentsHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    )
  },
  '/store': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute component={StorePageHandler} wrapperParams={{isFullScreen: true}} path={path} />
    )
  },
  '/store-closed': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={StoreClosedPageHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    )
  },
  '/store-open': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={StoreOpenPageHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    )
  },
  '/change-theme': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={ChangeThemeHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    )
  },
  '/debugStore': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={StorePageDebugHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    )
  },
  '/assignments/:subject/not-found': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={AssignmentNotFoundHandler} path={path} />
  },
  '/assignments/:subject/error': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={AssignmentErrorHandler} path={path} />
  },
  '/debuggers': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={DebuggersPageHandler} path={path} />
  },
  '/debuggers/math/question-skills-math-viewer': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={MathViewerHandler} path={path} />
  },
  '/onboarding': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={OnboardingPageHandler} path={path} />
  }
}
